import {
  ComponentGraphGroup,
  ComponentGraphNode,
  getAddedAndUpdatedItems,
  getComponentGraphNodeMetadata,
  getGraphNodeOrGroupLabelForWorkspaceMode,
  GraphItemsModel,
  RelationshipDiagramsViewInterface,
} from '@ardoq/graph';
import {
  APIComponentAttributes,
  APIReferenceAttributes,
  ArdoqId,
  FormattingFilter,
} from '@ardoq/api-types';
import { EnhancedScopeData, RepresentationData } from '@ardoq/data-model';
import {
  isComponentIncludedInContextByFilter,
  isReferenceIncludedInContextByFilter,
} from '@ardoq/filter-helpers';
import { enhancedScopeDataOperations } from '@ardoq/scope-data';
import { isTruthy } from '@ardoq/common-helpers';

/**
 * We check `.color` rather than `.included` because `included` reflects whether
 * the entity passes non-formatting (visibility) rules — always true for a single
 * FormattingFilter. `.color` is only set when the entity matches the filter's
 * own rules, making it the meaningful signal here.
 */
const componentMatchesColorFilter = (
  component: APIComponentAttributes,
  filter: FormattingFilter,
  enhancedScopeData: EnhancedScopeData
): boolean =>
  filter.affectComponent &&
  Boolean(
    isComponentIncludedInContextByFilter(
      component,
      [filter],
      component,
      enhancedScopeData
    ).color
  );

/** See {@link componentMatchesColorFilter} for why we check `.color`. */
const referenceMatchesColorFilter = (
  reference: APIReferenceAttributes,
  filter: FormattingFilter,
  enhancedScopeData: EnhancedScopeData
): boolean =>
  filter.affectReference &&
  Boolean(
    isReferenceIncludedInContextByFilter(reference, [filter], enhancedScopeData)
      .color
  );

type CreateDisconnectedContextNodeArgs = {
  viewInterface: RelationshipDiagramsViewInterface;
  componentId: ArdoqId;
  getRepresentationData: (componentId: ArdoqId) => RepresentationData;
};

export const getColorFormattingFilterIdsForComponent = (
  enhancedScopeData: EnhancedScopeData,
  formattingFilters: FormattingFilter[],
  componentId: ArdoqId
): string[] => {
  const component = enhancedScopeDataOperations.getComponent(
    enhancedScopeData,
    componentId
  );
  if (!component) return [];
  return formattingFilters
    .filter(filter =>
      componentMatchesColorFilter(component, filter, enhancedScopeData)
    )
    .map(filter => filter.id)
    .filter(isTruthy);
};

export const getColorFormattingFilterIdsForReference = (
  enhancedScopeData: EnhancedScopeData,
  formattingFilters: FormattingFilter[],
  referenceId: ArdoqId
): string[] => {
  const reference = enhancedScopeDataOperations.getReference(
    enhancedScopeData,
    referenceId
  );
  if (!reference) return [];
  return formattingFilters
    .filter(filter =>
      referenceMatchesColorFilter(reference, filter, enhancedScopeData)
    )
    .map(filter => filter.id)
    .filter(isTruthy);
};

type GetCandidateComponentIdsArgs = {
  nodes: GraphItemsModel<ComponentGraphNode>;
  groups: GraphItemsModel<ComponentGraphGroup>;
};

export const getCandidateComponentIds = ({
  nodes,
  groups,
}: GetCandidateComponentIdsArgs): ArdoqId[] =>
  [...getAddedAndUpdatedItems(groups), ...getAddedAndUpdatedItems(nodes)]
    .filter(item => item.isComponent())
    .map(({ modelId }) => modelId);

export const createDisconnectedContextNode = ({
  viewInterface,
  componentId,
  getRepresentationData,
}: CreateDisconnectedContextNodeArgs): ComponentGraphNode | null => {
  const rawGraphItem = { id: componentId, modelId: componentId };
  const metadata = getComponentGraphNodeMetadata({
    viewInterface,
    rawGraphItem,
    urlFieldValuesByComponentId: new Map(),
    representationDataByComponentId: new Map([
      [componentId, getRepresentationData(componentId)],
    ]),
    nodeColorsByGraphItemId: new Map([
      [
        componentId,
        viewInterface.getComponentDisplayColor(componentId, {
          useAsBackgroundStyle: false,
        }),
      ],
    ]),
    conditionalFormattingState: new Map(),
    nodeLabelsData: new Map([
      [componentId, getGraphNodeOrGroupLabelForWorkspaceMode(rawGraphItem)],
    ]),
    isContext: true,
  });
  return metadata
    ? ComponentGraphNode.create(componentId, componentId, metadata)
    : null;
};
